// src/components/pages/Contact.jsx
import React, { useState } from "react";

import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Field from "../ui/Field";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import { Toast } from "../ui/Spinner";

import axiosInstance from "../../utils/AxiosInstance";

/**
 * Contact form. Submits to /site/contact-message.
 *
 * Backend payload shape (matches the original frontend):
 *   { name, email, subject, message, type: "GENERAL", userId? }
 */
export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState({ open: false, tone: "success", message: "" });

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const onChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: undefined } : fe));
  };

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits.length ? `(${digits}` : "";
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onPhoneChange = (e) => {
    setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }));
    setFieldErrors((fe) => (fe.phone ? { ...fe, phone: undefined } : fe));
  };

  const validate = (f) => {
    const errs = {};
    if (!f.name.trim()) errs.name = "Please enter your name.";
    if (!f.email.trim()) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(f.email.trim())) errs.email = "Enter a valid email address.";
    const phoneDigits = f.phone.replace(/\D/g, "");
    if (phoneDigits.length > 0 && phoneDigits.length < 10) {
      errs.phone = "Enter a complete phone number, or leave this blank.";
    }
    if (!f.message.trim()) errs.message = "Please enter a message.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const errs = validate(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await axiosInstance.post(
        "/site/contact-message",
        {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          subject: form.phone ? `Phone: ${form.phone}` : "Contact form",
          message: form.message.trim(),
          type: "GENERAL",
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setSubmitted(true);
      setToast({ open: true, tone: "success", message: "Message sent — we'll be in touch." });
      setTimeout(() => setToast((t) => ({ ...t, open: false })), 3500);
    } catch (err) {
      const status = err?.response?.status;
      const violations = err?.response?.data?.violations;

      if (status === 400 && Array.isArray(violations) && violations.length) {
        const mapped = {};
        violations.forEach((v) => {
          const [field, ...rest] = String(v).split(":");
          const msg = rest.join(":").trim();
          if (field && msg && ["name", "email", "message"].includes(field.trim())) {
            mapped[field.trim()] = msg.charAt(0).toUpperCase() + msg.slice(1) + ".";
          }
        });
        if (Object.keys(mapped).length) {
          setFieldErrors(mapped);
        } else {
          setError("Couldn't send your message. Check your details and try again.");
        }
      } else if (status === 429) {
        setError("Too many messages sent — please wait a minute and try again.");
      } else if (status >= 500) {
        setError("Our system isn't reachable right now. Try again in a moment.");
      } else {
        setError("Couldn't send your message. Check your details and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <Toast open={toast.open} tone={toast.tone} message={toast.message} />

      <div className="px-4 sm:px-6 py-10 max-w-[760px] mx-auto">

        <header className="text-center mb-8">
          <h1
            className="font-semibold"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)", color: "var(--txt)" }}
          >
            Let's chat.
          </h1>
          <p className="text-[14px] mt-3" style={{ color: "var(--txt-2)" }}>
            Drop us a note and we'll get back within one business day.
          </p>
        </header>

        {submitted ? (
          <Card className="text-center py-10">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                background: "var(--teal-dim)",
                border: "1px solid var(--teal-border)",
                color: "var(--teal)",
              }}
            >
              <Icon name="check" size={28} />
            </div>
            <h2 className="font-semibold mb-2" style={{ fontSize: 20, color: "var(--txt)" }}>
              Message received.
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              Thanks for reaching out — we'll reply by email shortly.
            </p>
            <Button onClick={() => setSubmitted(false)}>Send another</Button>
          </Card>
        ) : (
          <Card>
            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <div
                  className="mb-4 p-3 rounded-[10px] text-[13px] flex items-start gap-2"
                  style={{
                    background: "var(--red-dim)",
                    border: "1px solid var(--red-border)",
                    color: "var(--red)",
                  }}
                >
                  <Icon name="alert-circle" size={14} style={{ marginTop: 2 }} />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-x-3">
                <Field label="Full name"   value={form.name}  onChange={onChange("name")}  placeholder="Your name" error={fieldErrors.name} />
                <Field label="Telephone"   value={form.phone} onChange={onPhoneChange} placeholder="(555) 123-4567" maxLength={14} error={fieldErrors.phone} />
              </div>
              <Field label="Email address" type="email" value={form.email} onChange={onChange("email")} placeholder="you@example.com" error={fieldErrors.email} />

              <div className="mb-3.5">
                <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
                  Message
                </label>
                <textarea
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="How can we help?"
                  rows={5}
                  className="w-full px-3.5 py-[11px] text-[14px] outline-none transition-colors rounded-[16px] border placeholder:text-[var(--txt-3)] resize-y"
                  style={{
                    background: "var(--surf-2)",
                    color: "var(--txt)",
                    borderColor: fieldErrors.message ? "var(--red-border)" : "var(--border-2)",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--gold)")}
                  onBlur={(e) => (e.target.style.borderColor = fieldErrors.message ? "var(--red-border)" : "var(--border-2)")}
                />
                {fieldErrors.message && (
                  <p className="mt-1.5 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                    <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.message}
                  </p>
                )}
              </div>

              <Button type="submit" loading={submitting} full>
                {!submitting && <Icon name="send" size={14} />}
                {submitting ? "Sending..." : "Send message"}
              </Button>

              <p className="text-[11px] text-center mt-3" style={{ color: "var(--txt-3)" }}>
                By submitting, you agree to our <a href="/privacy" style={{ color: "var(--gold)" }} className="hover:underline">Privacy Policy</a> and <a href="/terms" style={{ color: "var(--gold)" }} className="hover:underline">Terms of Use</a>.
              </p>
            </form>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
